<script lang="ts" setup>
	useHead({
		meta: [
			{name: 'theme-color', content: '#76B82A'},
			{name: 'msapplication-TileColor', content: '#76B82A'},
			{property: 'og:site_name', content: 'AWO Lausitz'},
			{property: 'og:type', content: 'website'},
			{property: 'og:locale', content: 'de_DE'}
		],
		link: [
			{rel: 'icon', type: 'image/png', sizes: '16x16', content: usePublicUrl('/icons/favicon-16x16.png')},
			{rel: 'icon', type: 'image/png', sizes: '32x32', content: usePublicUrl('/icons/favicon-32x32.png')},
			{rel: 'icon', type: 'image/png', sizes: '192x192', content: usePublicUrl('/icons/android-chrome-192x192.png')},
			{rel: 'icon', type: 'image/png', sizes: '512x512', content: usePublicUrl('/icons/android-chrome-512x512.png')},
			{rel: 'apple-touch-icon', content: usePublicUrl('/icons/apple-touch-icon.png')},
			{rel: 'msapplication-TileImage', content: usePublicUrl('/icons/tileImage-144x144.png')}
		]
	})
	const navigationItems = [
		{label: 'Aktuelles', name: 'news-list'},
		{label: 'Angebote', name: 'article-detail', slug: 'unsere-angebote'},
		{label: 'Standorte', name: 'article-detail', slug: 'unsere-standorte'},
		{label: 'Projekte', name: 'article-detail', slug: 'projektarbeit-und-foerderungen'},
		{label: 'Karriere', name: 'career-index'},
		{label: 'Über uns', name: 'article-detail', slug: 'ueber-uns'}
	]
	const footerLinkGroups = [
		{
			'title': 'Wichtige Links',
			'links': [
				{label: 'Stellenangebote', name: 'career-jobs'},
				{label: 'AWO | Lausitz als Arbeitgeber', name: 'career-employment'},
				{label: 'Ausbildung in der Pflege', name: 'article-detail', slug: 'ausbildung-in-der-pflege'},
				{label: 'Karriere', name: 'career-index'},
				{label: 'Mitarbeitendenportal', url: 'https://awolau.pi-asp.de/loga3/'}
			]
		},
		{
			'title': 'Unsere Angebote',
			'links': [
				{label: 'Kinder- und Jugendangebote', name: 'article-detail', slug: 'kinder-und-jugendliche'},
				{label: 'Altenpflege', name: 'article-detail', slug: 'senior-innen-und-pflege'},
				{label: 'Behindertenhilfe', name: 'article-detail', slug: 'menschen-mit-behinderung'},
				{label: 'Hilfen und Beratung', name: 'article-detail', slug: 'hilfen-und-beratung'}
			]
		},
		{
			'title': 'Über uns',
			'links': [
				{label: 'Standorte', name: 'article-detail', slug: 'unsere-standorte'},
				{label: 'Leitbild', name: 'article-detail', slug: 'unser-leitbild'},
				{label: 'Ansprechpersonen', name: 'article-detail', slug: 'kontaktpersonen'},
				{label: 'Kontakt', name: 'article-detail', slug: 'kontakt'},
				{label: 'Hinweisgeberschutzgesetz', name: 'article-detail', slug: 'hinweisgeberschutzgesetz'}
			]
		}
	]
	const footerLinks = [
		{label: 'Impressum', name: 'article-detail', slug: 'impressum'},
		{label: 'Datenschutz', name: 'article-detail', slug: 'datenschutzerklaerung'},
		{label: 'Barrierefreiheitserklärung', name: 'article-detail', slug: 'barrierefreiheitserklaerung'},
		{label: 'Kontakt', name: 'article-detail', slug: 'kontakt'}
	]

	const header = ref(null)
	const isScrolled = ref<boolean>(false)

	const route = useRoute()
	const drawerOpen = ref(false)

	onMounted(() => {
		useEventListener(window, 'scroll', function () {
			isScrolled.value = window.scrollY > header.value.clientHeight
		})
	})

	watch(route, value => {
		if(drawerOpen.value) {
			drawerOpen.value = false
		}
  	}, {deep: true, immediate: true})
</script>

<template>
	<GsApp color-mode-preference="light">
		<NuxtLayout	grid name="site-default">
			<template #header>
				<div ref="header"
					 :class="{'shadow-lg sm:!pt-2 lg:!pt-4 sm:!pb-2':isScrolled}"
					 class="fluid bg-white pt-2 sm:pt-4 lg:pt-8 pb-2 sm:pb-4 font-overpass transition duration-300 ease-in-out">
					<div class="">
						<div class="flex justify-between">
							<div :class="{'md:!w-[140px] lg:!w-[160px]':isScrolled}"
								 class="w-[120px] md:w-[160px] lg:w-[180px] my-auto pe-2 sm:pe-0">
								<router-link class="block" title="zur Startseite" to="/">
									<img v-lazy-load  alt="Logo der AWO Lausitz" class="w-full" data-src="@/assets/images/Logo-AWO-Lausitz.svg"
										 title=""/>
								</router-link>
							</div>
							<div class="flex justify-end">
								<NavigationMain  v-if="$screen.gte.lg" :items="navigationItems"></NavigationMain>
								<div class="flex justify-end my-auto space-x-2 lg:space-x-4">
									<LayoutContactPhone></LayoutContactPhone>
									<LayoutContactMail></LayoutContactMail>
									<div v-if="$screen.lt.lg" class="flex-none">
										<UDrawer direction="left" v-model:open="drawerOpen" title="Navigation">
											<GsBtn icon="fa6-solid-bars" class="flex! [&_span]:m-auto [&_span]:text-[22px] h-[48px] md:h-[52px] lg:h-[60px] p-0! aspect-square! border-primary bg-primary hover:bg-black! hover:border-black! shadow-lg" title="Navigation öffnen" aria-label="Navigation öffnen"></GsBtn>
											<template #header>
												<h2 class="sr-only">Navigation</h2>
											</template>
											<template #body>
												<div class="w-[90vw] 3xs:w-[320px] sm:w-[400px] bg-white h-full">
													<NavigationMobile :items="navigationItems" :footer-items="footerLinks.reverse()"></NavigationMobile>
												</div>
											</template>
										</UDrawer>
									</div>
								</div>
							</div>
						</div>
					</div>
				</div>
			</template>

			<div class="pt-[64px] sm:pt-[82px] md:pt-[99px] lg:pt-[123px]">
				<NuxtPage/>
			</div>

			<template #footer>
				<div class="fluid px-4 bg-gray-100">
					<div class="container pt-12 pb-6
							proseprose-p:text-black prose-p:text-[18px] md:prose-p:text-[20px] lg:prose-p:text-[22px] xl:prose-p:text-[24px] prose-p:not-italic prose-p:leading-[175%] prose-p:m-0
							prose-a:text-[18px] md:prose-a:text-[20px] lg:prose-a:text-[22px] xl:prose-a:text-[24px] prose-a:font-light prose-a:no-underline prose-a:block prose-a:leading-[175%]"
					>
						<div class="flex flex-col">
							<div class="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-4 lg:gap-x-10 xl:gap-x-16 gap-y-12	">
								<template v-if="footerLinkGroups &&  footerLinkGroups.length">
									<div v-for="(lg,idx) in footerLinkGroups" :key="idx"
										 class="text-center xl:text-left mb-12 xl:mb-0">
										<span class="block text-red text-[18px] md:text-[20px] lg:text-[22px] xl:text-[24px] font-medium mt-0 mb-4">{{ lg.title }}</span>
										<nav v-if="lg.links?.length" role="navigation" :aria-label="'Links zum Thema: ' + lg.title">
											<template v-for="(item,idx) in lg.links">
												<NuxtLink v-if="item.name" :title="`zur Seite ${item.label}`" :to="{name: item.name, params: item.slug ? { article_slug: item.slug } : {}}" no-prefetch>
													{{ item.label }}
												</NuxtLink>
												<a v-else-if="item.url" :href="item.url" target="_blank">{{ item.label}}</a>
											</template>
										</nav>
									</div>
								</template>
								<div class="mb-12 xl:mb-0 text-center xl:text-left">
									<span class="block text-red text-[18px] md:text-[20px] lg:text-[22px] xl:text-[24px] font-medium mt-0 mb-4">AWO | Lausitz</span>
									<address>
										<p>Thomas-Müntzer-Str. 26<br/>02977 Hoyerswerda</p>
									</address>
									<p>
										<a href="tel:+49357148850" title="Nummer anrufen">Tel.: 03571 4885-0</a>
										<span>Fax: 03571 4885-605</span>
									</p>
								</div>
							</div>
						</div>
						<div class="not-prose">
							<div class="xs:flex xs:justify-between mt-8 lg:mt-16">
								<nav v-if="footerLinks && footerLinks.length" class="flex flex-col sm:flex-row justify-center sm:justify-start gap-y-2.5 sm:gap-y-0 sm:gap-x-6 lg:gap-x-12 mb-6 sm:mb-0"
									 role="navigation" aria-label="Rechtliche Hinweise">
									<NuxtLink v-for="(item,idx) in footerLinks" class="text-center xs:text-left"
											  :title="item.label" :to="{name: item.name, params: {article_slug: item.slug }}"
											  no-prefetch>{{ item.label }}
									</NuxtLink>
								</nav>
								<a class="flex group" href="https://www.markov-markov.de" target="_blank">
									<span class="flex mx-auto xs:mx-none">
										<GsIcon class="flex-none text-gray group-hover:text-accent size-4 lg:size-5 my-auto me-2" name="fa-cogs"/>
										<span class="my-auto text-gray group-hover:text-accent text-[14px]">markov&markov</span>
									</span>
								</a>
							</div>
						</div>
					</div>
				</div>
			</template>
		</NuxtLayout>
	</GsApp>
</template>

<style>
	.site-grid {
		--padding-inline: theme('padding.4');
		--content-max-width: theme('screens.2xl');
		--breakout-max-width: 100%;
	}
	.container {
		max-width: var(--content-max-width);
	}

	header {
		position: fixed;
		left: 0;
		top: 0;
		width: 100%;
		z-index: 100;
	}
	header span > .router-link-active {
		color: var(--color-red);
	}
	header .flex > .router-link-active {
		text-decoration: underline;
	}
	footer nav a:hover,
	footer nav a:focus,
	footer nav .router-link-active {
		color: var(--color-red);
	}
	.page-enter-active,
	.page-leave-active {
		transition: all 0.15s;
	}
	.page-enter-from,
	.page-leave-to {
		opacity: 0;
		transform: scale(0.9);
		filter: blur(.5rem);
	}
	.font-outline-2 {
		-webkit-text-stroke: 2px white;
	}
</style>
